import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  JobCreationStatusResponse,
  JobCreationMessageStatus,
  JobStatusPayload,
} from '@techgitter/contracts';
import { updateJob, getJobCreationStatus, JobCreationError } from '../services/jobCreationService';
import type { JobUpdatePayload } from '../services/jobCreationService';
import { onSignalREvent } from '../services/signalRService';

const FALLBACK_POLL_INTERVAL_MS = 1_500;
const FALLBACK_MAX_POLL_MS = 10_000;
const MAX_POLL_DURATION_MS = 180_000;

export type UpdatePhase = 'idle' | 'submitting' | 'accepted' | 'succeeded' | 'failed';

interface UseJobUpdateResult {
  phase: UpdatePhase;
  messageId: string | null;
  status: JobCreationStatusResponse | null;
  error: string | null;
  submit: (jobGuid: string, payload: JobUpdatePayload) => Promise<void>;
  reset: () => void;
}

const TERMINAL_STATUSES: JobCreationMessageStatus[] = ['Succeeded', 'Failed', 'Dead Letter'];

export function useJobUpdate(): UseJobUpdateResult {
  const [phase, setPhase] = useState<UpdatePhase>('idle');
  const [messageId, setMessageId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobCreationStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pollStartRef = useRef(0);
  const resolvedRef = useRef(false);
  const msgIdRef = useRef<string | null>(null);
  const signalRUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      abortRef.current?.abort();
      signalRUnsubRef.current?.();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    signalRUnsubRef.current?.();
    signalRUnsubRef.current = null;
  }, []);

  const handleStatusUpdate = useCallback((statusResponse: JobCreationStatusResponse) => {
    if (resolvedRef.current) return;

    setStatus(statusResponse);

    if (TERMINAL_STATUSES.includes(statusResponse.status)) {
      resolvedRef.current = true;
      cleanup();

      if (statusResponse.status === 'Succeeded') {
        setPhase('succeeded');
      } else {
        setError(
          statusResponse.errors.length > 0
            ? statusResponse.errors.join('; ')
            : `Job update ${statusResponse.status.toLowerCase()}`
        );
        setPhase('failed');
      }
    }
  }, [cleanup]);

  const startFallbackPolling = useCallback((msgId: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    pollStartRef.current = Date.now();

    const poll = async () => {
      if (resolvedRef.current) return;

      if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        if (!resolvedRef.current) {
          resolvedRef.current = true;
          cleanup();
          setError('Ticket update timed out. Check the Alerts page for status.');
          setPhase('failed');
        }
        return;
      }

      try {
        const statusResponse = await getJobCreationStatus(msgId, controller.signal);
        console.info('[useJobUpdate] Fallback poll response: status=%s', statusResponse.status);
        handleStatusUpdate(statusResponse);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.warn('Fallback poll attempt failed:', err);
      }
    };

    const schedulePoll = (attempt: number) => {
      const delay = Math.min(
        FALLBACK_POLL_INTERVAL_MS * Math.pow(1.5, attempt),
        FALLBACK_MAX_POLL_MS
      );

      pollTimerRef.current = setTimeout(async () => {
        await poll();
        if (!resolvedRef.current) {
          schedulePoll(attempt + 1);
        }
      }, delay);
    };

    poll().then(() => {
      if (!resolvedRef.current) {
        schedulePoll(0);
      }
    });
  }, [cleanup, handleStatusUpdate]);

  const startSignalRListener = useCallback(() => {
    console.info('[useJobUpdate] SignalR listener registered (pre-submit)');
    const unsubscribe = onSignalREvent('jobs:status', (data: unknown) => {
      console.info('[useJobUpdate] SignalR jobs:status event received:', data);
      const payload = data as JobStatusPayload | undefined;
      const currentMsgId = msgIdRef.current;
      if (!payload || !currentMsgId || payload.messageId !== currentMsgId) return;
      console.info('[useJobUpdate] SignalR matched messageId, status=%s', payload.status);
      handleStatusUpdate(payload);
    });
    signalRUnsubRef.current = unsubscribe;
  }, [handleStatusUpdate]);

  const submit = useCallback(async (jobGuid: string, payload: JobUpdatePayload) => {
    setPhase('submitting');
    setError(null);
    setStatus(null);
    setMessageId(null);
    msgIdRef.current = null;
    resolvedRef.current = false;

    startSignalRListener();

    try {
      const result = await updateJob(jobGuid, payload);
      msgIdRef.current = result.messageId;
      setMessageId(result.messageId);
      setPhase('accepted');
      startFallbackPolling(result.messageId);
    } catch (err) {
      cleanup();
      const errorMessage = err instanceof JobCreationError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to update ticket';
      setError(errorMessage);
      setPhase('failed');
    }
  }, [startSignalRListener, startFallbackPolling, cleanup]);

  const reset = useCallback(() => {
    resolvedRef.current = true;
    cleanup();
    abortRef.current?.abort();
    setPhase('idle');
    setMessageId(null);
    setStatus(null);
    setError(null);
  }, [cleanup]);

  return {
    phase,
    messageId,
    status,
    error,
    submit,
    reset,
  };
}
